import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider } from '@mui/material';
import {
  backgroundSxForAppearance,
  createThemeForAppearance,
  prViewportFrameSx,
  readAppAppearance,
  writeAppAppearance,
} from './appAppearance';
import {
  isHologramChrome,
  readPrChromeStyle,
  writePrChromeStyle,
} from './hologramChrome';
import { emptyHoneycombColorMaps } from './honeycombCloudColors';

const AppAppearanceContext = createContext(null);

export function AppAppearanceProvider({ children }) {
  const [appearance, setAppearanceState] = useState(() => readAppAppearance());
  const [chromeStyle, setChromeStyleState] = useState(() => readPrChromeStyle());
  const [honeycombColorMaps, setHoneycombColorMaps] = useState(
    emptyHoneycombColorMaps
  );

  const setAppearance = useCallback((id) => {
    setAppearanceState(id);
    writeAppAppearance(id);
  }, []);

  const setChromeStyle = useCallback((style) => {
    setChromeStyleState(style);
    writePrChromeStyle(style);
  }, []);

  useLayoutEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const theme = createThemeForAppearance(appearance, honeycombColorMaps);
    meta.setAttribute('content', theme.palette.background.default);
  }, [appearance, honeycombColorMaps]);

  const theme = useMemo(
    () => createThemeForAppearance(appearance, honeycombColorMaps),
    [appearance, honeycombColorMaps]
  );

  const backgroundSx = useMemo(
    () => backgroundSxForAppearance(appearance, honeycombColorMaps),
    [appearance, honeycombColorMaps]
  );

  const honeycombRingColor = honeycombColorMaps.ring?.[appearance] || null;

  const hologram = useMemo(
    () => isHologramChrome(appearance, chromeStyle),
    [appearance, chromeStyle]
  );

  const viewportFrameSx = useMemo(
    () => (hologram ? prViewportFrameSx(appearance) : null),
    [appearance, hologram]
  );

  const value = useMemo(
    () => ({
      appearance,
      setAppearance,
      chromeStyle,
      setChromeStyle,
      hologram,
      backgroundSx,
      viewportFrameSx,
      honeycombColorMaps,
      setHoneycombColorMaps,
      honeycombRingColor,
    }),
    [
      appearance,
      setAppearance,
      chromeStyle,
      setChromeStyle,
      hologram,
      backgroundSx,
      viewportFrameSx,
      honeycombColorMaps,
      honeycombRingColor,
    ]
  );

  return (
    <AppAppearanceContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </AppAppearanceContext.Provider>
  );
}

export function useAppAppearance() {
  const ctx = useContext(AppAppearanceContext);
  if (!ctx) {
    throw new Error(
      'useAppAppearance muss innerhalb von AppAppearanceProvider verwendet werden'
    );
  }
  return ctx;
}
